import React, { useMemo } from "react";
import { Box, Heading, Stat, StatLabel, StatNumber, StatGroup, List, ListItem, Button, Icon, useColorModeValue, Table, Thead, Tbody, Tr, Th, Td, VStack, Text, Badge, Flex } from "@chakra-ui/react";
import { DownloadIcon, WarningIcon, CheckCircleIcon, InfoIcon } from '@chakra-ui/icons';
import { HStack } from '@chakra-ui/react';

function parseSpdx(json) {
  if (!json) return { version: "?", directCount: 0, transitiveCount: 0, licenses: [], licenseCounts: {}, licensePackages: {}, licensePackageVersions: {} };
  const version = json.spdxVersion || json.version || "?";
  const files = Array.isArray(json.files) ? json.files : [];
  const packages = Array.isArray(json.packages) ? json.packages : [];
  const relationships = Array.isArray(json.relationships) ? json.relationships : [];
  const licenseSet = new Set();
  const licenseCounts = {};
  const licensePackages = {};
  const licensePackageVersions = {};
  // Find main package (heuristic: first package with no 'DEPENDS_ON' relationship pointing to it)
  let mainPackageId = null;
  if (packages.length > 0) {
    // Try to find a package with no other package depending on it
    const allIds = packages.map(p => p.SPDXID);
    const dependedOn = new Set(relationships.filter(r => r.relationshipType === 'DEPENDS_ON').map(r => r.relatedSpdxElement));
    mainPackageId = allIds.find(id => !dependedOn.has(id)) || packages[0].SPDXID;
  }
  // Find direct dependencies (DEPENDS_ON from main package)
  const directDeps = new Set(
    relationships
      .filter(r => r.spdxElementId === mainPackageId && r.relationshipType === 'DEPENDS_ON')
      .map(r => r.relatedSpdxElement)
  );
  // All other packages that are not direct and not the main package are transitive
  const allDepIds = new Set(packages.map(p => p.SPDXID));
  const transitiveDeps = new Set(
    [...allDepIds].filter(id => id !== mainPackageId && !directDeps.has(id))
  );
  // License logic
  packages.forEach(pkg => {
    const lic = pkg.licenseConcluded || pkg.licenseDeclared || "?";
    if (!licenseCounts[lic]) licenseCounts[lic] = 0;
    licenseCounts[lic]++;
    licenseSet.add(lic);
    if (!licensePackages[lic]) licensePackages[lic] = [];
    licensePackages[lic].push(pkg.name || pkg.PackageName || pkg.SPDXID || "?");
    // Store version info for this package
    if (!licensePackageVersions[lic]) licensePackageVersions[lic] = [];
    licensePackageVersions[lic].push(pkg.versionInfo || pkg.version || pkg.PackageVersion || "?");
  });
  files.forEach(f => {
    if (Array.isArray(f.licenseInfoInFiles)) f.licenseInfoInFiles.forEach(l => {
      if (!licenseCounts[l]) licenseCounts[l] = 0;
      licenseCounts[l]++;
      licenseSet.add(l);
      if (!licensePackages[l]) licensePackages[l] = [];
      licensePackages[l].push(f.fileName || f.FileName || f.SPDXID || "?");
      if (!licensePackageVersions[l]) licensePackageVersions[l] = [];
      licensePackageVersions[l].push("?");
    });
    else if (f.licenseInfoInFiles) {
      if (!licenseCounts[f.licenseInfoInFiles]) licenseCounts[f.licenseInfoInFiles] = 0;
      licenseCounts[f.licenseInfoInFiles]++;
      licenseSet.add(f.licenseInfoInFiles);
      if (!licensePackages[f.licenseInfoInFiles]) licensePackages[f.licenseInfoInFiles] = [];
      licensePackages[f.licenseInfoInFiles].push(f.fileName || f.FileName || f.SPDXID || "?");
      if (!licensePackageVersions[f.licenseInfoInFiles]) licensePackageVersions[f.licenseInfoInFiles] = [];
      licensePackageVersions[f.licenseInfoInFiles].push("?");
    }
  });
  return {
    version,
    directCount: directDeps.size,
    transitiveCount: transitiveDeps.size,
    licenses: Array.from(licenseSet),
    licenseCounts,
    licensePackages,
    licensePackageVersions
  };
}

export default function SpdxWidget({ spdxJson, repo, build, filename, findings, bomber }) {
  const { version, directCount, transitiveCount, licenses, licenseCounts, licensePackages, licensePackageVersions } = useMemo(() => parseSpdx(spdxJson), [spdxJson]);
  const downloadUrl = repo && build && filename
    ? `/api/spdx/${repo}/${build}/${filename}`
    : undefined;
  const boxBg = useColorModeValue('white', 'gray.800');

  // Parse Bomber vulnerabilities from findings or bomber prop
  let bomberData = bomber || findings;
  let bomberVulns = [];
  if (bomberData && typeof bomberData === 'object' && Array.isArray(bomberData.packages)) {
    bomberData.packages.forEach(pkg => {
      if (Array.isArray(pkg.vulnerabilities)) {
        pkg.vulnerabilities.forEach(vuln => {
          bomberVulns.push({
            package: pkg.coordinates,
            cve: vuln.cve || vuln.id,
            severity: vuln.severity,
            title: vuln.title,
            description: vuln.description
          });
        });
      }
    });
  }

  // License classification
  const copyleft = [
    'GPL', 'AGPL', 'LGPL', 'MPL', 'EPL', 'CDDL', 'CC-BY-SA', 'CC-BY-NC-SA', 'CC-BY-ND', 'CC-BY-NC-ND', 'ODbL', 'OSL', 'QPL', 'SISSL', 'CPAL', 'RPL', 'Sleepycat', 'NPL', 'IPL', 'CECILL', 'Artistic-1.0', 'Artistic-2.0', 'GPL-2.0', 'GPL-3.0', 'LGPL-2.1', 'LGPL-3.0', 'AGPL-3.0', 'MPL-2.0', 'EPL-1.0', 'EPL-2.0', 'CDDL-1.0', 'CDDL-1.1', 'CC0-1.0', 'CC-BY-4.0', 'CC-BY-SA-4.0', 'CC-BY-NC-4.0', 'CC-BY-NC-SA-4.0', 'CC-BY-ND-4.0', 'CC-BY-NC-ND-4.0', 'ODbL-1.0', 'OSL-3.0', 'QPL-1.0', 'SISSL-1.2', 'CPAL-1.0', 'RPL-1.5', 'Sleepycat-1.0', 'NPL-1.1', 'IPL-1.0', 'CECILL-2.1', 'Artistic-1.0-Perl', 'Artistic-2.0-Perl'
  ];
  const permissive = [
    'MIT', 'BSD', 'BSD-2-Clause', 'BSD-3-Clause', 'Apache', 'Apache-2.0', 'ISC', 'Zlib', 'Python-2.0', 'Python-2.1', 'Python-2.2', 'Python-2.3', 'Python-2.4', 'Python-2.5', 'Python-2.6', 'Python-2.7', 'Python-3.0', 'Python-3.1', 'Python-3.2', 'Python-3.3', 'Python-3.4', 'Python-3.5', 'Python-3.6', 'Python-3.7', 'Python-3.8', 'Python-3.9', 'Python-3.10', 'Python-3.11', 'Python-3.12', 'Unlicense', '0BSD', 'AFL-3.0', 'Apache-1.1', 'Apache-1.0', 'BSD-4-Clause', 'ISC', 'MIT-0', 'MIT-CMU', 'MIT-advertising', 'MIT-enna', 'MIT-feh', 'MITNFA', 'Zlib', 'ZPL-2.1'
  ];
  const copyrightOrRequired = [
    'Proprietary', 'Commercial', 'Copyright', 'LicenseRef', 'Other', 'Custom', 'SEE LICENSE IN', 'SEE LICENSE FILE', 'SEE LICENSE', 'SEE LICENCE', 'SEE NOTICE', 'SEE README', 'SEE FILE', 'SEE DOC', 'SEE DOCUMENTATION', 'SEE DESCRIPTION', 'SEE DETAILS', 'SEE URL', 'SEE LINK', 'SEE WEBSITE', 'SEE SOURCE', 'SEE PROJECT', 'SEE REPOSITORY', 'SEE GITHUB', 'SEE BITBUCKET', 'SEE GITLAB', 'SEE BITBUCKET', 'SEE GIT', 'SEE SVN', 'SEE HG', 'SEE BAZAAR', 'SEE MERCURIAL', 'SEE CVS', 'SEE LICENSE.TXT', 'SEE LICENSE.MD', 'SEE LICENSE.RST', 'SEE LICENSE.HTML', 'SEE LICENSE.PDF', 'SEE LICENSE.DOC', 'SEE LICENSE.DOCX', 'SEE LICENSE.RTF', 'SEE LICENSE.ODT', 'SEE LICENSE.ODS', 'SEE LICENSE.ODP', 'SEE LICENSE.ODG', 'SEE LICENSE.ODF', 'SEE LICENSE.OTT', 'SEE LICENSE.OTS', 'SEE LICENSE.OTP', 'SEE LICENSE.OTG', 'SEE LICENSE.OTF', 'SEE LICENSE.OTC', 'SEE LICENSE.OTM', 'SEE LICENSE.OTN', 'SEE LICENSE.OTL', 'SEE LICENSE.OTB', 'SEE LICENSE.OTD', 'SEE LICENSE.OTK', 'SEE LICENSE.OTQ', 'SEE LICENSE.OTR', 'SEE LICENSE.OTS', 'SEE LICENSE.OTT', 'SEE LICENSE.OTV', 'SEE LICENSE.OTW', 'SEE LICENSE.OTX', 'SEE LICENSE.OTZ', 'SEE LICENSE.OU', 'SEE LICENSE.OV', 'SEE LICENSE.OW', 'SEE LICENSE.OX', 'SEE LICENSE.OY', 'SEE LICENSE.OZ', 'SEE LICENSE.PDF', 'SEE LICENSE.TXT', 'SEE LICENSE.MD', 'SEE LICENSE.RST', 'SEE LICENSE.HTML', 'SEE LICENSE.DOC', 'SEE LICENSE.DOCX', 'SEE LICENSE.RTF', 'SEE LICENSE.ODT', 'SEE LICENSE.ODS', 'SEE LICENSE.ODP', 'SEE LICENSE.ODG', 'SEE LICENSE.ODF', 'SEE LICENSE.OTT', 'SEE LICENSE.OTS', 'SEE LICENSE.OTP', 'SEE LICENSE.OTG', 'SEE LICENSE.OTF', 'SEE LICENSE.OTC', 'SEE LICENSE.OTM', 'SEE LICENSE.OTN', 'SEE LICENSE.OTL', 'SEE LICENSE.OTB', 'SEE LICENSE.OTD', 'SEE LICENSE.OTK', 'SEE LICENSE.OTQ', 'SEE LICENSE.OTR', 'SEE LICENSE.OTS', 'SEE LICENSE.OTT', 'SEE LICENSE.OTV', 'SEE LICENSE.OTW', 'SEE LICENSE.OTX', 'SEE LICENSE.OTZ', 'SEE LICENSE.OU', 'SEE LICENSE.OV', 'SEE LICENSE.OW', 'SEE LICENSE.OX', 'SEE LICENSE.OY', 'SEE LICENSE.OZ'
  ];

  function getLicenseType(license) {
    if (!license || license === '?' || license === 'NOASSERTION') return 'unknown';
    if (permissive.some(l => license.toUpperCase().includes(l.toUpperCase()))) return 'permissive';
    if (copyleft.some(l => license.toUpperCase().includes(l.toUpperCase()))) return 'copyleft';
    if (copyrightOrRequired.some(l => license.toUpperCase().includes(l.toUpperCase()))) return 'copyright';
    return 'other';
  }

  // License summary counts
  let summary = { copyleft: 0, copyright: 0 };
  licenses.forEach(lic => {
    if (lic === 'NOASSERTION') return;
    const type = getLicenseType(lic);
    if (type === 'copyleft') summary.copyleft += licenseCounts[lic] || 0;
    else if (type === 'copyright') summary.copyright += licenseCounts[lic] || 0;
  });

  return (
    <Box p={4} borderWidth={1} borderRadius="lg" boxShadow="md" bg={boxBg}>
      <Flex align="center" mb={4}>
        <StatGroup flex="1" align="center">
          <Stat>
            <StatLabel>SPDX Version</StatLabel>
            <StatNumber>{version}</StatNumber>
          </Stat>
          <Stat>
            <StatLabel>{transitiveCount > 0 ? 'Direct Dependencies' : 'Dependencies'}</StatLabel>
            <StatNumber>{directCount}</StatNumber>
          </Stat>
          {transitiveCount > 0 && (
            <Stat>
              <StatLabel>Transitive Dependencies</StatLabel>
              <StatNumber>{transitiveCount}</StatNumber>
            </Stat>
          )}
        </StatGroup>
        {downloadUrl && (
          <Box ml={4} display="flex" alignItems="center" height="100%">
            <Button as="a" href={downloadUrl} leftIcon={<DownloadIcon />} colorScheme="blue" variant="outline" download size="sm">
              Download SPDX
            </Button>
          </Box>
        )}
      </Flex>
      {/* Vulnerability count per severity (Bomber) */}
      {bomberData && bomberData.summary && Object.values(bomberData.summary).some(count => count > 0) && (
        <Box mb={4}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>Detected Vulnerabilities</Text>
          <HStack spacing={2}>
            {['Critical', 'High', 'Moderate', 'Low', 'Unspecified'].map(sev => (
              bomberData.summary[sev] > 0 && (
                <Badge key={sev} colorScheme={sev === 'Critical' ? 'red' : sev === 'High' ? 'orange' : sev === 'Moderate' ? 'yellow' : sev === 'Low' ? 'gray' : 'gray'} fontSize="xs" cursor="default">
                  {sev.toUpperCase()}: {bomberData.summary[sev]}
                </Badge>
              )
            ))}
          </HStack>
        </Box>
      )}
      
      {/* Licenses Section - only show if there are licenses */}
      {licenses.filter(lic => lic !== 'NOASSERTION').length > 0 && (
        <Box mb={4}>
          <Text fontSize="sm" fontWeight="medium" mb={2}>Licenses</Text>
          <HStack wrap="wrap" spacing={2}>
            {licenses.filter(lic => lic !== 'NOASSERTION').map((lic, i) => {
              const type = getLicenseType(lic);
              let icon = null;
              let color = 'gray';
              let label = '';
              if (type === 'permissive') {
                icon = <CheckCircleIcon color="green.400" mr={1} />;
                color = 'green';
                label = 'Permissive';
              } else if (type === 'copyleft') {
                icon = <WarningIcon color="orange.400" mr={1} />;
                color = 'orange';
                label = 'Copyleft';
              } else if (type === 'copyright') {
                icon = <WarningIcon color="red.400" mr={1} />;
                color = 'red';
                label = 'Proprietary/Required';
              } else {
                icon = <InfoIcon color="gray.400" mr={1} />;
                color = 'gray';
                label = 'Other';
              }
              return (
                <Badge key={i} colorScheme={color} variant="subtle" px={2} py={0.5} fontSize="xs" display="flex" alignItems="center" cursor="default">
                  {icon}
                  {lic} <span style={{ marginLeft: 4, fontWeight: 400, fontSize: '0.75em', color: `var(--chakra-colors-${color}-400)` }}>{label}</span>
                </Badge>
              );
            })}
          </HStack>
        </Box>
      )}
      {/* Non-Permissive Licenses Section */}
      {licenses.filter(lic => lic !== 'NOASSERTION' && (getLicenseType(lic) === 'copyleft' || getLicenseType(lic) === 'copyright')).length > 0 && (
        <Box mt={4}>
          <Heading size="sm" mb={2}>Non-Permissive Licenses</Heading>
          {licenses.filter(lic => lic !== 'NOASSERTION' && (getLicenseType(lic) === 'copyleft' || getLicenseType(lic) === 'copyright')).map((lic, idx) => (
            <Box key={lic} mb={4}>
              <Heading size="xs" mb={2}>{lic} Affected Modules</Heading>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Module/Package</Th>
                    <Th>Version</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(licensePackages[lic] || []).map((pkg, i) => (
                    <Tr key={i}>
                      <Td>{pkg}</Td>
                      <Td>{(licensePackageVersions[lic] && licensePackageVersions[lic][i]) || '?'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ))}
        </Box>
      )}
      {/* Bomber Vulnerabilities Section */}
      {bomberData && (
        <Box mt={4}>
          <Heading size="sm" mb={2}>Detected Vulnerabilities</Heading>
          {bomberVulns.length > 0 ? (
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Package</Th>
                  <Th>CVE</Th>
                  <Th>Severity</Th>
                  <Th>Title</Th>
                </Tr>
              </Thead>
              <Tbody>
                {bomberVulns.map((v, i) => (
                  <Tr key={i}>
                    <Td>{v.package}</Td>
                    <Td>{v.cve}</Td>
                    <Td><Badge colorScheme={v.severity === 'CRITICAL' ? 'red' : v.severity === 'HIGH' ? 'orange' : v.severity === 'MODERATE' ? 'yellow' : v.severity === 'LOW' ? 'gray' : 'gray'}>{v.severity}</Badge></Td>
                    <Td>{v.title}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <Box p={3} color="green.700" fontSize="md" fontWeight="medium" bg="green.100" borderRadius="md" textAlign="center" border="1px solid" borderColor="green.200">
              🎉 No security vulnerabilities found! 🎉
            </Box>
          )}
          {/* Bomber tool info footer */}
          {bomberData && bomberData.meta && (
            <Box mt={2} textAlign="right" fontSize="xs" textTransform="uppercase" color="gray.400" letterSpacing="wider">
              {bomberData.meta.generator || 'BOMBER'}{bomberData.meta.version ? ` v${bomberData.meta.version}` : ''} | {bomberData.meta.provider || 'CVE'}
            </Box>
          )}
        </Box>
      )}
      {/* Bomber Findings Section */}
      {findings && Array.isArray(findings) && findings.length > 0 && (
        <Box mt={4}>
          <Heading size="sm" mb={2}>Bomber Findings</Heading>
          <VStack align="stretch" spacing={1}>
            {findings.map((finding, i) => (
              <Box key={i} p={2} bg="orange.900" color="orange.100" borderRadius="md" fontSize="sm">
                {finding}
              </Box>
            ))}
          </VStack>
        </Box>
      )}
      {findings && Array.isArray(findings) && findings.length > 0 && (
        <Box mt={4}>
          <Heading size="sm" mb={2}>SBOM Findings</Heading>
          <VStack align="stretch" spacing={1}>
            {findings.map((finding, i) => (
              <Box key={i} p={2} bg="orange.900" color="orange.100" borderRadius="md" fontSize="sm">
                {finding}
              </Box>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
} 